import {
  fetchFormularies,
  fetchFormularyGroups,
} from '@/screens/Tickets/hooks/useFetchFormularies';
import { cacheGet, cacheSet } from './groupedSelectCache';
import { usePaginationObserver } from '../misc/DialogSelect/utils';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  chakra,
  Checkbox,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BiSearch } from 'react-icons/bi';

import { SelectionMode } from '@react-types/shared';

import { TBaseItem } from '../misc/DialogSelect/useDialogSelect';

type SelectorProps = {
  value?: string[];
  initialOptions?: TBaseItem[];
  selectionMode?: SelectionMode;
  triggerIds?: string[];
  currentTriggerableIds?: string[];
  onChange?: (value: TBaseItem[]) => void;
  onSelectionChange?: (keys: string[]) => void;
  [key: string]: unknown;
};

type FormularyItem = { id: string; name: string };
type GroupSection = { id: string; name: string; formularies: FormularyItem[] };
type CacheValue = { sections: GroupSection[]; cursor?: string; hasMore: boolean };

const PAGE_SIZE = 200;

function mergeFormularyItems(
  prev: GroupSection[],
  groupNameMap: Record<string, string>,
  newItems: Array<{ id: string; name?: string | null; formularyGroupId?: string | null }>,
): GroupSection[] {
  const sectionsMap = new Map(
    prev.map((s) => [s.id, { ...s, formularies: [...s.formularies] }]),
  );
  const newGroupIds: string[] = [];

  newItems.forEach((f) => {
    const hasGroup = f.formularyGroupId && groupNameMap[f.formularyGroupId];
    const groupId = hasGroup ? f.formularyGroupId! : 'no_group';
    const groupName = hasGroup ? groupNameMap[f.formularyGroupId!] : 'Sem grupo';

    if (sectionsMap.has(groupId)) {
      sectionsMap.get(groupId)!.formularies.push({ id: f.id, name: f.name ?? '' });
    } else {
      sectionsMap.set(groupId, {
        id: groupId,
        name: groupName,
        formularies: [{ id: f.id, name: f.name ?? '' }],
      });
      newGroupIds.push(groupId);
    }
  });

  const existing = prev.map((s) => sectionsMap.get(s.id)!);
  const newGroups = newGroupIds
    .map((id) => sectionsMap.get(id)!)
    .sort((a, b) => a.name.localeCompare(b.name));

  return [...existing, ...newGroups];
}

function GroupRow({
  group,
  selectedIds,
  selectionMode,
  onToggleGroup,
  onToggleItem,
}: {
  group: GroupSection;
  selectedIds: Set<string>;
  selectionMode?: SelectionMode;
  onToggleGroup: (ids: string[]) => void;
  onToggleItem: (id: string) => void;
}) {
  const ids = group.formularies.map((f) => f.id);
  const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
  const allChecked = selectedCount === ids.length && ids.length > 0;
  const isIndeterminate = selectedCount > 0 && !allChecked;

  return (
    <AccordionItem border="none">
      <h2>
        <AccordionButton px="2" py="2">
          <Box flex="1" textAlign="left">
            {selectionMode !== 'single' && selectionMode !== 'none' ? (
              <Checkbox
                isChecked={allChecked}
                isIndeterminate={isIndeterminate}
                onChange={() => onToggleGroup(ids)}
                onClick={(e) => e.stopPropagation()}
                fontWeight="semibold"
              >
                {group.name} ({group.formularies.length})
              </Checkbox>
            ) : (
              <chakra.span fontWeight="semibold" fontSize="sm">
                {group.name} ({group.formularies.length})
              </chakra.span>
            )}
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel ms="6" pb={4} pt={1}>
        <List spacing={2}>
          {group.formularies.map((formulary) => (
            <ListItem key={formulary.id}>
              <Checkbox
                isChecked={selectedIds.has(formulary.id)}
                onChange={() => onToggleItem(formulary.id)}
                size="md"
              >
                {formulary.name}
              </Checkbox>
            </ListItem>
          ))}
        </List>
      </AccordionPanel>
    </AccordionItem>
  );
}

export function GroupedFormularySelect({
  value,
  initialOptions,
  selectionMode,
  triggerIds,
  currentTriggerableIds,
  onChange,
  onSelectionChange,
}: SelectorProps) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(
    new Set(value ?? []),
  );
  const [search, setSearch] = React.useState('');
  const [groups, setGroups] = React.useState<GroupSection[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [cursor, setCursor] = React.useState<string | undefined>();
  const [hasMore, setHasMore] = React.useState(false);
  const [expandedIndices, setExpandedIndices] = React.useState<number[]>([]);

  React.useEffect(() => {
    setSelectedIds(new Set(value ?? []));
  }, [value]);

  const cacheKey = `formulary:${JSON.stringify({ triggerIds, currentTriggerableIds })}`;

  const buildInitialSections = (
    groupData: Array<{ id: string; name?: string | null }>,
    items: Array<{ id: string; name?: string | null; formularyGroupId?: string | null }>,
    currentSelectedId?: string,
  ): GroupSection[] => {
    const groupNameMap = Object.fromEntries(groupData.map((g) => [g.id, g.name ?? '']));
    const byGroup: Record<string, FormularyItem[]> = {};
    const ungrouped: FormularyItem[] = [];

    items.forEach((f) => {
      if (f.formularyGroupId && groupNameMap[f.formularyGroupId]) {
        byGroup[f.formularyGroupId] = byGroup[f.formularyGroupId] ?? [];
        byGroup[f.formularyGroupId].push({ id: f.id, name: f.name ?? '' });
      } else {
        ungrouped.push({ id: f.id, name: f.name ?? '' });
      }
    });

    const namedGroups = groupData
      .filter((g) => (byGroup[g.id]?.length ?? 0) > 0)
      .map((g) => ({ id: g.id, name: g.name ?? '', formularies: byGroup[g.id] }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const noGroup: GroupSection[] = ungrouped.length
      ? [{ id: 'no_group', name: 'Sem grupo', formularies: ungrouped }]
      : [];

    const selectedGroupIdx = currentSelectedId
      ? namedGroups.findIndex((g) => g.formularies.some((f) => f.id === currentSelectedId))
      : -1;

    if (selectedGroupIdx >= 0) {
      const sel = namedGroups[selectedGroupIdx];
      const others = namedGroups.filter((_, i) => i !== selectedGroupIdx);
      return [sel, ...noGroup, ...others];
    }
    return [...noGroup, ...namedGroups];
  };

  const loadMore = React.useCallback(async () => {
    if (!hasMore || loadingMore || !cursor) return;
    setLoadingMore(true);
    const [groupData, { items, cursor: newCursor }] = await Promise.all([
      fetchFormularyGroups(),
      fetchFormularies({ first: PAGE_SIZE, after: cursor, triggerIds, currentTriggerableIds }),
    ]);
    const groupNameMap = Object.fromEntries(groupData.map((g) => [g.id, g.name ?? '']));
    setGroups((prev) => mergeFormularyItems(prev, groupNameMap, items));
    setCursor(newCursor);
    setHasMore(!!newCursor);
    setLoadingMore(false);
  }, [hasMore, loadingMore, cursor, triggerIds, currentTriggerableIds]);

  // Pre-fetch on mount
  React.useEffect(() => {
    const cached = cacheGet<CacheValue>(cacheKey);
    if (cached) {
      setGroups(cached.sections);
      setCursor(cached.cursor);
      setHasMore(cached.hasMore);
      return;
    }
    setLoading(true);
    Promise.all([
      fetchFormularyGroups(),
      fetchFormularies({ first: PAGE_SIZE, triggerIds, currentTriggerableIds }),
    ]).then(([groupData, { items, cursor: newCursor }]) => {
      const sections = buildInitialSections(groupData, items);
      setCursor(newCursor);
      setHasMore(!!newCursor);
      setGroups(sections);
      setLoading(false);
    });
  }, []);

  // Persist to cache whenever state settles
  React.useEffect(() => {
    if (loading || loadingMore || groups.length === 0) return;
    cacheSet(cacheKey, { sections: groups, cursor, hasMore });
  }, [groups, cursor, hasMore, loading, loadingMore]);

  // Re-order on modal open: move selected group to top
  React.useEffect(() => {
    if (!isOpen || groups.length === 0) return;
    const currentSelectedId = Array.from(selectedIds)[0];
    if (!currentSelectedId) return;
    setGroups((prev) => {
      const alreadyFirst = prev[0]?.formularies.some((f) => f.id === currentSelectedId);
      if (alreadyFirst) return prev;
      const idx = prev.findIndex((g) => g.formularies.some((f) => f.id === currentSelectedId));
      if (idx <= 0) return prev;
      const reordered = [...prev];
      const [sel] = reordered.splice(idx, 1);
      reordered.unshift(sel);
      return reordered;
    });
  }, [isOpen]);

  // Load more when all groups are collapsed (and not searching)
  React.useEffect(() => {
    if (expandedIndices.length === 0 && hasMore && !loadingMore && groups.length > 0 && !search) {
      loadMore();
    }
  }, [expandedIndices]);

  const filteredGroups = React.useMemo(() => {
    if (!search) return groups;
    return groups
      .map((g) => ({
        ...g,
        formularies: g.formularies.filter((f) =>
          f.name.toLowerCase().includes(search.toLowerCase()),
        ),
      }))
      .filter((g) => g.formularies.length > 0);
  }, [groups, search]);

  React.useEffect(() => {
    setExpandedIndices(filteredGroups.map((_, i) => i));
  }, [filteredGroups]);

  const { observerTarget } = usePaginationObserver({ onPaginate: loadMore });

  const toggleGroup = React.useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const toggleItem = React.useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (selectionMode === 'single' || selectionMode === 'none') {
          next.clear();
          next.add(id);
        } else if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [selectionMode],
  );

  const allFormularies = React.useMemo(
    () => groups.flatMap((g) => g.formularies),
    [groups],
  );

  const selectedItems = React.useMemo(() => {
    const pool = [
      ...allFormularies,
      ...(initialOptions ?? []).map((o) => ({ id: o.id, name: o.name })),
    ];
    return Array.from(selectedIds)
      .map((id) => pool.find((f) => f.id === id))
      .filter((f): f is FormularyItem => !!f);
  }, [selectedIds, allFormularies, initialOptions]);

  const handleConfirm = () => {
    const result: TBaseItem[] = selectedItems.map((f) => ({
      id: f.id,
      name: f.name,
      data: {},
    }));
    onChange?.(result);
    onSelectionChange?.(Array.from(selectedIds));
    onClose();
  };

  return (
    <>
      <Button
        onClick={onOpen}
        display="flex"
        fontWeight="normal"
        size="md"
        variant="outline"
        alignItems="center"
        justifyContent="space-between"
        flexDir="row"
        width="full"
        whiteSpace="nowrap"
        position="relative"
        overflow="hidden"
        ps="2"
        pe="3"
      >
        <chakra.span
          wordBreak="break-all"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          overflow="hidden"
        >
          {selectedItems.length
            ? selectedItems.map((i) => i.name).join(', ')
            : t('placeholders.select_ellipsis')}
        </chakra.span>
        <chakra.span w="4">
          <Icon as={ChevronDownIcon} fontSize="2xl" />
        </chakra.span>
      </Button>

      <Modal size="2xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('selects.form')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody p="0">
            <chakra.div
              overflowY="clip"
              w="full"
              p="6"
              maxH="500px"
              minH="300px"
              display="flex"
              gap="4"
              flexDir="column"
            >
              <InputGroup>
                <Input
                  placeholder={t('placeholders.search')}
                  onChange={(e) => setSearch(e.target.value)}
                  backgroundColor="white"
                />
                <InputRightElement>
                  <BiSearch color="#E2E8F0" />
                </InputRightElement>
              </InputGroup>

              <chakra.div overflowY="auto">
                {loading ? (
                  <chakra.div w="full" py="4" display="flex" justifyContent="center">
                    <Spinner />
                  </chakra.div>
                ) : (
                  <>
                    <Accordion
                      allowMultiple
                      index={expandedIndices}
                      onChange={(indices) => setExpandedIndices(indices as number[])}
                    >
                      {filteredGroups.map((group) => (
                        <GroupRow
                          key={group.id}
                          group={group}
                          selectedIds={selectedIds}
                          selectionMode={selectionMode}
                          onToggleGroup={toggleGroup}
                          onToggleItem={toggleItem}
                        />
                      ))}
                    </Accordion>
                    {hasMore && !search && <chakra.div ref={observerTarget} />}
                    {loadingMore && (
                      <chakra.div w="full" py="2" display="flex" justifyContent="center">
                        <Spinner size="sm" />
                      </chakra.div>
                    )}
                  </>
                )}
              </chakra.div>
            </chakra.div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="solid"
              colorScheme="brand"
              w="full"
              size="md"
              onClick={handleConfirm}
            >
              {t('common.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}